var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/* global __webpack_init_sharing__, __webpack_share_scopes__ */
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useReducer } from 'react';
import * as Yup from 'yup';
import { getIsValidUrl } from '../ducks/util/urls';
var initialPluginState = { ready: false, failed: false };
var initialState = { plugins: {} };
function reducer(state, action) {
    var _a, _b, _c;
    switch (action.type) {
        case 'init':
            if (_.has(state.plugins, action.url))
                return state;
            return {
                plugins: __assign(__assign({}, state.plugins), (_a = {}, _a[action.url] = initialPluginState, _a)),
            };
        case 'loaded':
            return {
                plugins: __assign(__assign({}, state.plugins), (_b = {}, _b[action.url] = { ready: true, failed: false }, _b)),
            };
        case 'failed':
            return {
                plugins: __assign(__assign({}, state.plugins), (_c = {}, _c[action.url] = { ready: true, failed: true }, _c)),
            };
    }
}
var PluginContext = React.createContext({
    plugins: {},
    initPlugin: function () { },
    pluginLoaded: function () { },
    pluginFailed: function () { },
});
var PluginProvider = function (_a) {
    var children = _a.children;
    var _b = useReducer(reducer, initialState), state = _b[0], dispatch = _b[1];
    var initPlugin = function (url) {
        dispatch({ type: "init" /* PluginReducerActionType.Init */, url: url });
    };
    var pluginLoaded = function (url) {
        dispatch({ type: "loaded" /* PluginReducerActionType.Loaded */, url: url });
    };
    var pluginFailed = function (url) {
        dispatch({ type: "failed" /* PluginReducerActionType.Failed */, url: url });
    };
    return (React.createElement(PluginContext.Provider, { value: { plugins: state.plugins, initPlugin: initPlugin, pluginLoaded: pluginLoaded, pluginFailed: pluginFailed } }, children));
};
var usePlugin = function (pluginConfig) {
    var _a = useContext(PluginContext), plugins = _a.plugins, initPlugin = _a.initPlugin, pluginLoaded = _a.pluginLoaded, pluginFailed = _a.pluginFailed;
    var url = pluginConfig.url;
    useEffect(function () {
        if (_.get(plugins, url)) {
            return; // already loaded or loading: nothing to do
        }
        initPlugin(pluginConfig.url);
        var element = document.createElement('script');
        element.src = url + '?cacheBuster=' + new Date().getTime();
        element.type = 'text/javascript';
        element.async = true;
        element.onload = function () {
            pluginLoaded(pluginConfig.url);
        };
        element.onerror = function () {
            pluginFailed(pluginConfig.url);
        };
        document.head.appendChild(element);
    }, [url]);
    return _.get(plugins, url, initialPluginState);
};
/**
 * Use a plugin locally, not adding it to the loaded plugins.
 * This is mainly used when configuring the application and not intended for the data area.
 *
 * **Note:** The script is not loaded if {@link getIsValidUrl} returns `false` for the given URL, and none of the
 *  callbacks are called in that scenario. It's up to the caller to handle the URL being invalid, but this is a failsafe
 *  because hooks cannot be called conditionally, and this hook is usually used when configuring the URL.
 */
export var usePluginLocally = function (_a) {
    var url = _a.url, onStart = _a.onStart, onLoad = _a.onLoad, onError = _a.onError;
    var element = useMemo(function () {
        // Safeguard in case the caller passes a bad URL, because hooks can't be called conditionally.
        // We leave it up to the caller to show useful information when the URL is invalid, so that they won't
        //  see a "Couldn't load script" error while really there was a URL issue.
        if (!getIsValidUrl(url) || _.isEmpty(url))
            return null;
        var element = document.createElement('script');
        element.type = 'text/javascript';
        element.src = "".concat(url, "?cacheBuster=").concat(new Date().getTime());
        element.async = true;
        return element;
    }, [url]);
    useEffect(function () {
        if (!element)
            return;
        onStart();
        document.head.appendChild(element);
        return function () {
            element.remove();
        };
        // Don't want to trigger this whenever onStart changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [element]);
    // Assign `onload` and `onerror` in a separate block, because changes to these hooks shouldn't re-load the script.
    useEffect(function () {
        if (element) {
            element.onload = onLoad;
            element.onerror = onError;
        }
    }, [onLoad, onError, element]);
};
/**
 * Function to load modules (usually React components for plugins) from an external module, using
 * module federation.
 *
 * @param scope Scope of the remote component, as defined in its webpack.config
 * @param module Name of the module exported by the remote component
 * @returns Promise which will resolve to the ES module
 */
export function loadExternalComponent(scope, module) {
    return __awaiter(this, void 0, void 0, function () {
        var container, factory;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: 
                // @ts-ignore
                return [4 /*yield*/, __webpack_init_sharing__('default')];
                case 1:
                    // @ts-ignore
                    _a.sent();
                    container = window[scope];
                    // @ts-ignore
                    return [4 /*yield*/, container.init(__webpack_share_scopes__.default)];
                case 2:
                    // @ts-ignore
                    _a.sent();
                    return [4 /*yield*/, container.get(module)];
                case 3:
                    factory = _a.sent();
                    return [2 /*return*/, factory()];
            }
        });
    });
}
/**
 * Validation scheme to ensure that the configuration that plugins provide to
 * Tarasa is actually valid.
 */
var PLUGIN_CONFIGURATION_VALIDATION_SCHEMA = Yup.object().shape({
    requiredVersion: Yup.number().required().min(1).max(2).default(1),
});
var DEFAULT_FORMATTER_CONFIGURATION_STATE = {
    initialized: true,
    configuration: {},
    error: null,
};
export var useFormatterConfiguration = function (formatter, validationSchema) {
    if ((formatter === null || formatter === void 0 ? void 0 : formatter.componentType) === "PluginFormatter" /* FormatterComponentType.PluginFormatter */) {
        return usePluginConfiguration(formatter.settings.pluginConfig, validationSchema);
    }
    return DEFAULT_FORMATTER_CONFIGURATION_STATE;
};
/**
 * Returns the Tarasa configuration for the given plugin. Currently, this is only the requiredVersion, which can be used
 * to provide backward compatibility, or to give an error when the required version is higher than the current version.
 */
export var usePluginConfiguration = function (pluginConfig, validationSchema) {
    var _a = React.useState({
        initialized: false,
        configuration: null,
        error: null,
    }), pluginRequirements = _a[0], setPluginRequirements = _a[1];
    var loadPlugin = usePlugin(pluginConfig);
    useEffect(function () {
        if (!loadPlugin.ready || loadPlugin.failed || pluginRequirements.initialized)
            return;
        loadExternalComponent(pluginConfig.scope, pluginConfig.module).then(function (Component) {
            var rawConfiguration = _.defaultTo(Component.default.tarasaConfiguration, {});
            try {
                var parsedConfiguration = (validationSchema !== null && validationSchema !== void 0 ? validationSchema : PLUGIN_CONFIGURATION_VALIDATION_SCHEMA).validateSync(rawConfiguration);
                setPluginRequirements({
                    initialized: true,
                    configuration: parsedConfiguration,
                    error: null,
                });
            }
            catch (err) {
                setPluginRequirements({
                    initialized: false,
                    configuration: null,
                    error: err.errors,
                });
            }
        });
    }, [loadPlugin, pluginConfig, pluginRequirements.initialized]);
    if (loadPlugin.failed)
        return {
            initialized: false,
            configuration: null,
            error: true,
        };
    if (pluginRequirements.error) {
        return {
            initialized: false,
            configuration: null,
            error: pluginRequirements.error,
        };
    }
    if (!pluginRequirements.initialized)
        return {
            initialized: false,
            configuration: null,
            error: null,
        };
    return {
        initialized: true,
        configuration: pluginRequirements.configuration,
        error: null,
    };
};
export { PluginProvider, usePlugin };
